import { Grid } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import React, { useEffect, useState } from 'react';
import { pageScrollDown } from '../SummaryPage';
import {
  CardComponent,
  ContractRiskDescription,
  ContractRiskScore,
  DottedText,
  PageTitleAndSubTitle,
} from '@HomaHealth/ui-component-library/lib/_AIQ-components';
import { patientAccessTheme } from '@HomaHealth/ui-component-library/lib/theme/theme';
import { AlertColor as MuiColor } from '@mui/material/Alert';
import axios from 'axios';
import Loader from '../../_components/molecules/Loader';
import { observer } from 'mobx-react';
import { useStores } from '../../stores';
import { toJS } from 'mobx';
import FileUploader from '../../_components/organisms/FileUploader';
import Snackbar from '../../_components/atoms/Snackbar';
import contractAIQConstants from '../../core-utils/messages/ContractAIQConstants';
import properties from '../../core-utils/properties';
import { LeftArrow } from '@HomaHealth/ui-component-library';
import { getTextWithToolTip } from '../../core-utils/helper-utils/riskPatientDetailsHelper';

export interface contractAiqAnalysisProps {}

const ContractAiqAnalysis: React.FC<contractAiqAnalysisProps> = observer(
  ({}) => {
    const messages = contractAIQConstants;

    const [pageTitle, setPageTitle] = useState<any>('');
    const [pageSubTitle, setPageSubTitle] = useState<any>('');
    const [metaData, setMetaData] = useState<any>(messages.METADATA);
    const [loading, setLoading] = useState<boolean>(false);
    const [loader] = useState<any>(<Loader />);
    const [overAllScore, setOverAllScore] = useState<number>(0);
    const [riskDetails, setRiskDetails] = useState<any>(messages.RISKDATA);
    const [snackbarState, setSnackbarState] = React.useState<{
      open: boolean;
      message: string;
      severity: MuiColor | undefined;
      duration: null | number;
    }>({
      open: false,
      message: '',
      severity: undefined,
      duration: null,
    });

    const fileUploadStore = useStores().fileUploadStore;

    const useStyles = makeStyles()({
      root: {
        display: 'flex',
        flexDirection: 'column',
        padding: '70px',
        gap: '30px',
      },
      uploadRoot: {
        display: 'flex',
        flexDirection: 'column',
        padding: '40px 0px 66px 63px',
      },
      separator: {
        width: '100%',
        height: '1px',
        backgroundColor: patientAccessTheme.palette.grey[300],
        border: 'none',
      },
    });
    const { classes } = useStyles();

    const getProgressBarColor = (value: number) => {
      // 0-40 is red, 40-80 is orange and 80+ is green
      if (value < 40) {
        return `#ED9174`;
      } else if (value >= 40 && value < 80) {
        return `#EDB574`;
      } else {
        return `#08C7B4`;
      }
    };

    const createNamespace = async (fileUrl: string) => {
      const filename = fileUrl.split('/').pop() || '';

      const filenameWithoutExtension = filename
        .split('.')
        .slice(0, -1)
        .join('.');

      try {
        setLoading(true);
        const res = await axios.post(
          properties.chainBaseUrl + properties.createNamespace,
          {
            files: [
              {
                file_url: fileUrl,
              },
            ],
            splitter_conf: {
              chunk_overlap: 200,
              chunk_size: 1000,
              heading_level: 1,
              length_function: 'Token',
              parent_chunk_overlap: 800,
              parent_chunk_size: 4000,
              parent_length_function: 'Token',
              parent_splitter_type: 'RecursiveCharacter',
              splitter_type: 'RecursiveCharacter',
            },
            pinecone_namespace_conf: {
              add_parent_doc: true,
              hybrid_bm25: false,
              hybrid_splade: false,
              pinecone_index: 'vector-db-test',
              pinecone_namespace: filenameWithoutExtension + '-parent',
            },
            embedding_model_conf: {
              embedding_model_name: 'text_embedding_ada_002_prod',
              embedding_model_source: 'Azure',
            },
          },
        );

        if (res?.status === 200) {
          fileUploadStore.setSelectedNamespace(
            filenameWithoutExtension + '-parent',
          );
        }
      } catch (error: any) {
        handleOpenSnackBar(
          messages.CREATE_NAMESPACE_ERROR(error?.status ?? '500'),
          'error',
          5000,
        );
        setLoading(false);
        console.error(error);
      }
    };

    const getMetaData = async (
      name: string,
      description: string,
      key_value: string,
      object_type: string,
      key_word: string,
    ) => {
      try {
        const res = await axios.post(
          properties.chainBaseUrl + properties.standardChain,
          {
            files: null,
            prompt_conf: {
              system_instructions:
                "You are a helpful, respectful and honest assistant. Always answer as helpfully as possible, while being safe. Your answers should not include any harmful, unethical, racist, sexist, toxic, dangerous, or illegal content. Please ensure that your responses are socially unbiased and positive in nature.\n\nIf a question does not make any sense, or is not factually coherent, explain why instead of answering something not correct. If you don't know the answer to a question, please don't share false information.",
              prompt_template: `Given the following Document. ---------------- Document: {document} ---------------- Extract metadata from Document and output as only json format as per below details with object type mentioned and against the key name mentioned below. Give the output as 'No' in json output, if you do not find the answer for that particular metadata from the Document. Do not give any extra information, just the JSON output. \nMetadata: ${name}\nDescription of Metadata: ${description} \nKey: ${key_value}\nObject Type: ${object_type}`,
            },
            contract_search_terms: key_word,
            contract_retriever_conf: {
              pinecone_index: 'vector-db-test',
              pinecone_namespace: toJS(fileUploadStore.selectedNamespace),
              retriever_k: 4,
              retriever_search_type: 'similarity',
            },
            embedding_model_conf: {
              embedding_model_name: 'text_embedding_ada_002_prod',
              embedding_model_source: 'Azure',
            },
            llm_model_conf: {
              llm_kwargs: {
                model_kwargs: {
                  seed: 100,
                },
                model_source: 'OpenAI',
              },
              llm_model_name: 'gpt-4-1106-preview',
              llm_model_source: 'OpenAI',
            },
          },
        );

        if (res?.data?.answer) {
          const startIndex = res.data.answer.indexOf('{');
          const endIndex = res.data.answer.lastIndexOf('}');
          const jsonString = res.data.answer.substring(
            startIndex,
            endIndex + 1,
          );

          const parsedObject = JSON.parse(jsonString);
          const answer = parsedObject?.[key_value].toString();
          const capitalized = answer
            ? answer?.charAt(0).toUpperCase() + answer?.slice(1)
            : '-';

          return {
            name: name,
            description: description,
            key_value: key_value,
            object_type: object_type,
            value: getTextWithToolTip(capitalized, 20),
            key_word: key_word,
          };
        }
      } catch (error) {
        handleOpenSnackBar(messages.METADATA_ERROR(name), 'error', 5000);
        console.error(error);
      }

      return {
        name: name,
        description: description,
        key_value: key_value,
        object_type: object_type,
        value: '-',
        key_word: key_word,
      };
    };

    const getRiskDetailsAPI = async (obj: any) => {
      try {
        const res = await axios.post(
          properties.chainBaseUrl + properties.standardChain,
          {
            files: [
              {
                file_url: toJS(fileUploadStore.uploadedFile),
              },
            ],
            prompt_conf: {
              system_instructions:
                "You are a helpful, respectful and honest assistant. Always answer as helpfully as possible, while being safe. Your answers should not include any harmful, unethical, racist, sexist, toxic, dangerous, or illegal content. Please ensure that your responses are socially unbiased and positive in nature.\n\nIf a question does not make any sense, or is not factually coherent, explain why instead of answering something not correct. If you don't know the answer to a question, please don't share false information.",
              prompt_template:
                "Given the following standard and its corresponding clauses and the document ---------------- Standard: {standard} Clauses: {clauses} ---------------- Document: {document} ---------------- Check if the document meets the standard. If there are Exhibits mentioned in a particular clause in the document, evaluate the clause in conjunction with the Exhibit. Do not suggest any clauses outside of those mentioned in the above Clauses. The output of the the response post going through the Document should be in json format with key values being: 'Existing Clauses Meeting Standards', 'Clauses Needing Amendment to Meet Standards', 'Missing Clauses'. Under each of the key values also mention why you have selected those clauses with two key values namely 'Clause' and 'Reason'. None of the values mentioned against 'Clause' key should be more then 5 words. Once the answer is ready, check if you are mentioning anything in the value against the key value 'Clause' outside of Clauses. If yes, remove them from the list. At last one more key value to json output showing overall compliance score of Document for the standard based on analysis with key 'Compliance Score' and the value for showing should be in terms of %. Do not give any extra information, just the JSON output.",
            },
            standard_conf: {
              clauses: obj.input_clauses,
              standard: obj.summary[0].description[0],
            },
            contract_search_terms: null,
            contract_retriever_conf: null,
            embedding_model_conf: {
              embedding_model_name: 'text_embedding_ada_002_prod',
              embedding_model_source: 'Azure',
            },
            llm_model_conf: {
              llm_kwargs: {
                model_kwargs: {
                  seed: 100,
                },
                model_source: 'OpenAI',
              },
              llm_model_name: 'gpt-4-1106-preview',
              llm_model_source: 'OpenAI',
            },
          },
        );

        if (res?.data?.answer) {
          const startIndex = res.data.answer?.indexOf('{');
          const endIndex = res.data.answer?.lastIndexOf('}');
          const jsonString = res.data.answer?.substring(
            startIndex,
            endIndex + 1,
          );
          const parsedObj = jsonString ? JSON.parse(jsonString) : null;

          obj.clauseCompliance.forEach((compliance: any) => {
            if (parsedObj) {
              const relatedData = parsedObj[compliance.title];
              const dataArray = Array.isArray(relatedData)
                ? relatedData
                : [relatedData];
              if (dataArray) {
                compliance.details = dataArray.map(
                  (item: any) =>
                    `${item?.Clause ?? ''} : ${item?.Reason ?? ''}`,
                );
              }

              const numericValue = parseFloat(parsedObj['Compliance Score']);
              const integerValue = Math.round(numericValue);

              obj.score = integerValue ?? 0;
              obj.progressBarColor = getProgressBarColor(integerValue ?? 0);
            } else {
              obj.score = 0;
              obj.progressBarColor = getProgressBarColor(0);
            }
          });

          setOverAllScore((prev: number) => prev + obj.score);
          return obj;
        }
      } catch (error) {
        handleOpenSnackBar(messages.RISKDATA_ERROR(obj.title), 'error', 5000);
        console.error(error);
      }

      return obj;
    };

    const getDocumentSummary = async () => {
      try {
        const res = await axios.post(
          properties.chainBaseUrl + properties.standardChain,
          {
            files: [
              {
                file_url: toJS(fileUploadStore.uploadedFile),
              },
            ],
            prompt_conf: {
              system_instructions:
                "You are a helpful, respectful and honest assistant. Always answer as helpfully as possible, while being safe. Your answers should not include any harmful, unethical, racist, sexist, toxic, dangerous, or illegal content. Please ensure that your responses are socially unbiased and positive in nature.\n\nIf a question does not make any sense, or is not factually coherent, explain why instead of answering something not correct. If you don't know the answer to a question, please don't share false information.",
              prompt_template:
                'Given the following document ---------------- Document: {document} Write a summary of the contract as a paragraph in 2-3 lines bringing out what are the services being provided in the contract and who are the parties involved. Start with: The agreement is between',
            },
            contract_search_terms: null,
            contract_retriever_conf: null,
            embedding_model_conf: {
              embedding_model_name: 'text_embedding_ada_002_prod',
              embedding_model_source: 'Azure',
            },
            llm_model_conf: {
              llm_kwargs: {
                model_kwargs: {
                  seed: 100,
                },
                model_source: 'OpenAI',
              },
              llm_model_name: 'gpt-4-1106-preview',
              llm_model_source: 'OpenAI',
            },
          },
        );

        if (res?.data) {
          setPageSubTitle(res?.data?.answer ?? '');
        }
      } catch (error) {
        handleOpenSnackBar(messages.SUMMARY_ERROR, 'error', 5000);
        console.error(error);
      }
    };

    useEffect(() => {
      if (fileUploadStore.selectedNamespace) {
        setPageTitle('Pharmacy Benefit Management Services Agreement');
        setOverAllScore(0);
        getDocumentSummary();

        setLoading(true);
        const updatedDataPromises1 = metaData.map((obj: any) =>
          getMetaData(
            obj.name,
            obj.description,
            obj.key_value,
            obj.object_type,
            obj.key_word,
          ),
        );
        const updatedDataPromises2 = riskDetails.map((obj: any) =>
          getRiskDetailsAPI(obj),
        );

        Promise.all([...updatedDataPromises1, ...updatedDataPromises2])
          .then((updatedData) => {
            const firstPart = updatedData.slice(
              0,
              updatedData.length - messages.RISKDATA.length,
            );
            const secondPart = updatedData.slice(
              updatedData.length - messages.RISKDATA.length,
            );

            setMetaData(firstPart);
            setRiskDetails(secondPart);

             
            setOverAllScore((prev) =>
              Math.round(prev / messages.RISKDATA.length),
            );
          })
          .catch((error) => console.error(error))
          .finally(() => {
            setLoading(false);
          });
      }
    }, [fileUploadStore.selectedNamespace]);

    const handleOpenSnackBar = (
      message: string,
      severity: MuiColor,
      duration: number | null,
    ) => {
      setSnackbarState({
        open: true,
        message: message,
        severity: severity,
        duration: duration,
      });
    };

    const handleCloseSnackBar = (
      event?: React.SyntheticEvent,
      reason?: string,
    ) => {
      if (reason === 'clickaway') {
        return;
      }
      setSnackbarState({
        open: false,
        message: '',
        severity: undefined,
        duration: null,
      });
    };

    return (
      <>
        {toJS(fileUploadStore.selectedNamespace) ? (
          loading ? (
            loader
          ) : (
            <div>
              <Grid container className={classes.root}>
                <Grid
                  container
                  style={{ cursor: 'pointer', width: 'fit-content' }}
                  onClick={() => {}}
                  sx={{
                    alignItems: 'center',
                  }}
                >
                  <Grid
                    style={{
                      height: '14px',
                      width: '14px',
                      marginRight: '8px',
                    }}
                  >
                    <LeftArrow
                      height="14px"
                      width="14px"
                      fill={patientAccessTheme.palette.primary.main}
                    />
                  </Grid>
                  <Grid onClick={() => fileUploadStore.removeFileData()}>
                    <DottedText text={messages.GO_BACK} />
                  </Grid>
                </Grid>
                <ContractRiskScore
                  title={pageTitle}
                  subTitle={pageSubTitle}
                  metaData={metaData}
                  overallScore={overAllScore}
                  progressBarColor={getProgressBarColor(overAllScore)}
                  showTooltip={false}
                  headerGridStyles={{
                    padding: '20px 24px 24px 20px',
                  }}
                />
                {riskDetails.length &&
                  riskDetails?.map((item: any) => (
                    <ContractRiskDescription
                      title={item.title}
                      summary={item.summary}
                      clauseCompliance={item.clauseCompliance}
                      score={item.score}
                      progressBarColor={item.progressBarColor}
                    />
                  ))}
              </Grid>

              <div id="scroll-top-element" />
              {pageScrollDown('scroll-top-element')}
            </div>
          )
        ) : (
          <Grid container className={classes.uploadRoot}>
            <Grid
              style={{
                marginBottom: '36px',
                width: '1182px',
                position: 'relative',
                display: 'flex',
                flexDirection: 'column',
                gap: '2rem',
              }}
            >
              <PageTitleAndSubTitle
                title={messages.TITLE}
                subTitle={messages.SUBTITLE}
              />
              <Grid>
                <CardComponent
                  title="Upload File"
                  body={
                    <FileUploader
                      activeTabIndex={0}
                      activeTabName="Contract"
                      isFileDetailsNeeded={true}
                      isFileLinkUploadSupported={false}
                      fileTypeSupported={['.pdf']}
                      handleOpenSnackBar={handleOpenSnackBar}
                      handleStageCompletion={() => {}}
                      createNamespace={createNamespace}
                      setLoading={setLoading}
                    />
                  }
                  width="642px"
                />
              </Grid>
            </Grid>
          </Grid>
        )}
        <Snackbar
          {...snackbarState}
          handleClose={handleCloseSnackBar}
          vertical="bottom"
          horizontal="right"
        />
      </>
    );
  },
);
export default ContractAiqAnalysis;
